import { useCallback, useState } from 'react';
import { TargetCatalogEntryInfo } from 'ais-facade-api/api';
import styles from './AtonActionCell.module.scss';
import { Button, Dropdown, Icon } from '..';
import { useAddToFleetMutation } from 'app/api/fleet';
import { useAppDispatch, useAppSelector } from 'app/store/store';
import { useGetVesselCatalogMutation, useLazyGetHistoryQuery } from 'app/api/facade';
import { Link, useNavigate } from 'react-router-dom';
import { DropDownItem } from '../Dropdown';

import { useTranslation } from 'react-i18next';
import { setFindAton, setFindVessel } from 'app/utils/selectedTargetsSlice';
import clsx from 'clsx';

interface Props {
  vessel: TargetCatalogEntryInfo;
}

export const AtonActionCell = ({ vessel }: Props) => {
  const { t } = useTranslation();

  const user = useAppSelector((state) => state.userSlice.user);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [dropdown, setDropdown] = useState(false);
  const [, catalogRequest] = useGetVesselCatalogMutation({
    fixedCacheKey: 'get-vessels-mutation',
  });

  const canBeShown = typeof vessel?.position?.latitude === 'number' && typeof vessel.position.longitude === 'number';

  const [addToFleet] = useAddToFleetMutation();
  const [downloadHistory] = useLazyGetHistoryQuery();

  const handleDownload = useCallback(() => {
    if (typeof vessel?.mmsi === 'number') {
      downloadHistory(vessel?.mmsi)
        .unwrap()
        .then(({ url, filename }) => {
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', filename);
          document.body.appendChild(link);

          link.click();

          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        });
    }
  }, [vessel]);

  const handleAddToFleet = () => {
    const user_id = user?.value.sub;

    if (user_id && typeof vessel?.mmsi === 'number') {
      addToFleet({ mmsiArray: [vessel.mmsi] })
        .unwrap()
        .then(() => {
          catalogRequest.reset();
        });
    }

    setDropdown(false);
  }

  const showVessel = () => {
    const isAton = vessel?.registryData?.specialType && vessel.registryData.specialType === 'ATON';
    if (isAton) {
      dispatch(setFindAton(vessel));
    } else dispatch(setFindVessel(vessel));
    navigate('/mapview');
  }

  return (
    <>
      <Button
        variant="icon-only"
        onClick={() => setDropdown((prev) => !prev)}
        aria-expanded={dropdown ? 'true' : 'false'}
      >
        <Icon name="more-icon" color="var(--default-primary)" width="13px" height="3px" />
      </Button>

      <Dropdown
        dropdown={dropdown}
        onClickOutside={() => {
          setDropdown(false);
        }}
      >
        <DropDownItem>
          <button
            className={
              clsx(styles.button, {
                [styles.disabled]: !canBeShown
              })
            }
            aria-disabled={!canBeShown}
            disabled={!canBeShown}
            onClick={canBeShown ? showVessel : undefined}
          >
            {t('ShowOnMap')}
          </button>
        </DropDownItem>

        <DropDownItem>
          <Link to={`/vessels/${vessel.mmsi}`}>{t('AtonInfo')}</Link>
        </DropDownItem>

        <DropDownItem>
          <button
            className={clsx(styles.button, { [styles.disabled]: vessel.belongsToFleet })}
            aria-disabled={vessel.belongsToFleet}
            disabled={vessel.belongsToFleet}
            onClick={!vessel.belongsToFleet ? handleAddToFleet : undefined}
          >
            {t('ButtonLabelAddVesselToFleet')}
          </button>
        </DropDownItem>

        <DropDownItem>
          <button
            className={clsx(styles.button, { [styles.disabled]: !handleDownload })}
            aria-disabled={!handleDownload}
            disabled={!handleDownload}
            onClick={handleDownload}
          >
            {t('ButtonLabelDownloadHistory')}
          </button>
        </DropDownItem>
      </Dropdown>
    </>
  );
};
